import { useInitialMonth, orderMonthOffset } from './usePinnedOrderMonth.js';
import { useExtraDraft } from '../store/extraDrafts.js';
/**
 * Раздел «Сотрудники» — §7 и расчёт зарплат §4.
 *
 * Раздел — ТОЛЬКО для владельца. Ограничение продублировано на сервере:
 * сотруднику не приезжает ни сам список, ни ставки с премиями, и пакет
 * изменений от не-владельца отклоняется. Скрыть пункт в меню недостаточно.
 *
 * `canEdit` ниже остаётся намеренно: он закрывает каждый отдельный элемент
 * управления. Если проверка на входе когда-нибудь регрессирует, правки всё
 * равно не пройдут.
 *
 * Раздел устроен в два слоя, и это существенно:
 *
 *   1. ЗАРПЛАТЫ за выбранный месяц — сколько выходит каждому и сколько
 *      остаётся владельцу. Ставка правится ползунком, и рядом сразу видно,
 *      во что обходится её изменение. Прежде ставка была числом в ячейке
 *      таблицы: «20 %» ничего не говорит, пока не посчитаешь вручную.
 *   2. УЧЁТНЫЕ ЗАПИСИ — логин, доступ, пароль. Это про вход в программу,
 *      а не про деньги, и мешать их в одной таблице незачем.
 *
 * Доход месяца берётся из тех же заказов и той же формулы, что и раздел
 * «Финансы» (`@otc/shared/finance`): два разных ответа на вопрос «сколько
 * я заработал» — худшее, что может быть в программе про деньги.
 */

import { useEffect,useMemo, useState, type CSSProperties } from 'react';
import { Check, Eye, EyeOff, KeyRound, Lock, Pencil, Plus, Trash2, Unlock, Users, X } from 'lucide-react';
import {
  formatKop,
  formatMonthKey,
  formatPercent,
  isFirstMonth,
  monthKeyForOffset,
  monthKeysOfYear,
  monthKeysUpTo,
  monthTotals,
  monthlyTrend,
  taxForMonth,
  payroll,
  rublesToKop,
  salaryOf,
  shareApplies,
  employeeColors,
  todayIso,
} from '@otc/shared';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api/client.js';
import { useBootstrap, useInvoices, useMe, useOrders, useMonthExpenses,
  useSalaryOverrides } from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useSync } from '../store/useSync.js';
import { useModule } from '../store/useModule.js';
import { Button, ErrorState, Field, Modal, SectionHeader, Spinner, Toast, useToast } from '../components/ui.js';
import { SaveDiscard } from '../components/SaveDiscard.js';
import { ConflictModal } from '../components/GuardModals.js';
import { EditableText } from '../components/EditableText.js';
import { STATUS_COLORS } from '../theme.js';
import { MonthSwitcher } from '../components/MonthSwitcher.js';
import { ВидЗарплаты } from './MySalary.js';
import { CardWindow } from '../components/CardWindow.js';
import { useTableZoom } from '../components/useTableZoom.js';
import { ZoomControl } from '../components/ZoomControl.js';

interface EmployeeRow {
  id: string; version: number; name: string;
  /* «Менеджер» убран 25.09.2026: такой должности в лаборатории нет. */
  position: 'Техник' | 'Администратор';
  role: 'OWNER' | 'EMPLOYEE';
  login: string; blocked: boolean; hasPassword: boolean;
  percent?: number; bonusKop?: number;
  /** Видит ли сотрудник свою зарплату — раздел «Моя зарплата». */
  salaryAccess?: boolean;
}

const MONO = { fontFamily: "'IBM Plex Mono', monospace", color: 'var(--otc-ink)' };

/**
 * На сколько месяцев ключ отстоит от нынешнего — обратная `monthKeyForOffset`.
 *
 * Нужна щелчку по столбику в кабинете: диаграмма называет месяц ключом, а
 * состояние окна хранит смещение.
 */
function смещениеМесяца(today: string, ключ: string): number {
  const [г, м] = ключ.split('-').map(Number) as [number, number];
  const [гНын, мНын] = today.slice(0, 7).split('-').map(Number) as [number, number];
  return (г - гНын) * 12 + (м - мНын);
}


/**
 * Запись месяца по сотруднику: что назначено вручную и что выдано авансом.
 *
 * `amountKop === null` — итог считается по проценту; запись существует
 * только ради аванса.
 */
interface ManualSalary {
  bonusKop: number;
  amountKop: number | null;
  advanceKop: number;
  /** Отпускные месяца — добавка сверх зарплаты. */
  vacationKop: number;
  note?: string;
}

export function Employees() {
  const today = todayIso();
  const initialMonth = useInitialMonth(today);
  const me = useMe();
  const bootstrap = useBootstrap(true);
  // Доход месяца считается по всем заказам — как в разделе «Финансы».
  const ordersQuery = useOrders(null);
  /* Счета — база налога, а он входит в доход, от которого считается доля. */
  const invoicesQuery = useInvoices(true);
  const module = useModule('employees');
  const { toast, show } = useToast();

  /* Масштаб рабочей области: у каждого раздела свой, запоминается в браузере. */
  const zoom = useTableZoom('employees', true);

  const [offset, setOffset] = useState(() => orderMonthOffset(today, initialMonth));
  const [span, setSpan] = useState(6);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [accountFor, setAccountFor] = useState<string | null>(null);

  /*
   * КАБИНЕТ СОТРУДНИКА — тот же экран, что видит он сам.
   *
   * Владельцу нужно знать, что именно увидит человек, прежде чем открывать
   * ему доступ: одно дело числа в своей таблице, другое — чужой экран с
   * подписями. Пароль здесь не спрашивается: все эти числа владелец и так
   * видит строкой выше, и запирать их от него — обряд без смысла.
   *
   * Месяц в кабинете свой: смотрят обычно не тот, что открыт в разделе.
   */
  const [кабинет, setКабинет] = useState<string | null>(null);
  const [кабинетСмещение, setКабинетСмещение] = useState(0);

  const canEdit = me.data?.user.role === 'OWNER';

  useSync('employees', 'employee', bootstrap.data?.employees as never);

  const draftState = useDrafts((state) => state.modules.employees);
  const employees = useMemo(() => {
    void draftState;
    return useDrafts.getState().entities<EmployeeRow>('employees', 'employee');
  }, [draftState]);

  /**
   * Фактические зарплаты, назначенные вручную.
   *
   * Расчёт по проценту и премии описывает, как платить дальше. За закрытые
   * месяцы платили иначе — сдельно, по своим таблицам учёта, — и никакая
   * формула этих сумм не воспроизводит. Здесь владелец ставит выплату прямо:
   * назначенная сумма заменяет расчёт целиком.
   */
  const salariesQuery = useSalaryOverrides(canEdit);

  /*
   * Прочие расходы месяца берутся и здесь: «Доход за месяц» в этом разделе
   * обязан совпадать с «Финансами». Доля сотрудников от них не зависит —
   * она считается от базы до налога и до прочих трат.
   */
  const otherQuery = useMonthExpenses(canEdit);
  const otherByMonth = useMemo(() => {
    const map = new Map<string, number>();
    for (const запись of otherQuery.data?.expenses ?? []) map.set(запись.monthKey, запись.amountKop);
    return map;
  }, [otherQuery.data]);
  const прочие = (ключ: string) => otherByMonth.get(ключ) ?? 0;
  const [salaryBusy, setSalaryBusy] = useState(false);

  const orders = useMemo(
    () =>
      (ordersQuery.data?.orders ?? []).map((order) => ({
        received: order.received,
        costKop: order.costKop,
        expenseKop: order.expenseKop,
        // Статус нужен ради «Ожидания»: замороженный заказ выпадает из расчёта.
        status: order.status,
      })),
    [ordersQuery.data],
  );

  const update = (id: string, patch: Record<string, unknown>) =>
    useDrafts.getState().update('employees', 'employee', id, patch);

  /**
   * Записать фактическую выплату за открытый месяц.
   *
   * Мимо черновика модуля: это не правка ставки, которую копят и фиксируют
   * пачкой, а запись о состоявшемся событии — деньги уже отданы.
   */
  /*
   * ПРАВКИ ПО ЗАРПЛАТАМ ЖДУТ КНОПКУ «СОХРАНИТЬ».
   *
   * Сначала аванс и выплата уходили на сервер сразу по вводу — и это
   * оказалось неожиданным: весь раздел живёт черновиком, наверху стоят
   * «Сохранить» и «Отменить», а два поля из карточки вели себя иначе и
   * записывались молча. Отменить такую правку было нечем.
   *
   * Теперь они копятся здесь, рядом с правками ставок и премий, и уходят
   * тем же нажатием. Ключ — сотрудник; запись всегда полная, потому что
   * на сервер она уходит целиком: сумма, аванс и пояснение.
   */
  const [pendingSalary, setPendingSalary] = useState<
    Map<
      string,
      { amountKop: number | null; advanceKop: number; vacationKop: number; bonusKop: number; note: string }
    >
  >(new Map());

  // Черновики хранят месяц в ключе: листание не переносит и не стирает премии.
  /** Запись месяца с учётом ещё не сохранённых правок. */
  const записьМесяца = (employeeId: string) => {
    const ожидает = pendingSalary.get(`${monthKey}|${employeeId}`);
    if (ожидает) return ожидает;
    const серверная = manualThisMonth.get(employeeId);
    return {
      amountKop: серверная?.amountKop ?? null,
      advanceKop: серверная?.advanceKop ?? 0,
      vacationKop: серверная?.vacationKop ?? 0,
      bonusKop: серверная?.bonusKop ?? 0,
      note: серверная?.note ?? '',
    };
  };

  const правитьЗапись = (
    employeeId: string,
    patch: Partial<{
      amountKop: number | null;
      advanceKop: number;
      vacationKop: number;
      bonusKop: number;
      note: string;
    }>,
  ) => {
    setPendingSalary((current) => {
      const серверная = manualThisMonth.get(employeeId);
      const key = `${monthKey}|${employeeId}`;
      const прежняя = current.get(key) ?? {
        amountKop: серверная?.amountKop ?? null,
        advanceKop: серверная?.advanceKop ?? 0,
        vacationKop: серверная?.vacationKop ?? 0,
      bonusKop: серверная?.bonusKop ?? 0,
        note: серверная?.note ?? '',
      };
      const next = new Map(current);
      next.set(key, { ...прежняя, ...patch });
      return next;
    });
  };

  /**
   * Отправка накопленных правок по зарплатам — вместе с «Сохранить» раздела.
   *
   * Каждая правка хранит свой месяц. Явные нули снимают начисления,
   * а null в ручной сумме возвращает автоматический расчёт.
   */
  const flushSalaries = async (): Promise<boolean> => {
    if (pendingSalary.size === 0) return true;
    setSalaryBusy(true);
    try {
      for (const [key, запись] of pendingSalary) {
        const [salaryMonth, employeeId] = key.split('|');
        // Явные нули снимают премию/аванс/отпускные; DELETE возвращает лишь расчёт.
        await api.put(`/api/salaries/${employeeId}/${salaryMonth}`, запись);
      }
      setPendingSalary(new Map());
      await salariesQuery.refetch();
      return true;
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось сохранить зарплаты', 'error');
      return false;
    } finally {
      setSalaryBusy(false);
    }
  };
  useExtraDraft('employees', pendingSalary.size > 0, {
    save: flushSalaries,
    discard: () => setPendingSalary(new Map()),
  });

  const save = async () => {
    const outcome = await module.save();
    if (!outcome.ok) {
      if (outcome.error) show(outcome.error, 'error');
      return;
    }
    // Зарплаты идут ВТОРЫМИ: они ссылаются на сотрудников, и новый
    // сотрудник должен успеть получить настоящий ключ.
    /* Удача — молча: строки сами показывают новые суммы. */
    await flushSalaries();
  };

  const setEmployeePassword = async (id: string, name: string, password: string) => {
    try {
      await api.post(`/api/employees/${id}/password`, { password });
      show(`Пароль для «${name}» назначен, доступ открыт`);
      void bootstrap.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось назначить пароль', 'error');
    }
  };

  /*
   * Ждём и зарплаты с прочими расходами — как в «Финансах».
   *
   * Без них первая отрисовка считает выплаты по проценту, а не по факту, и
   * «Остаётся вам» на мгновение показывает не ту сумму и не ту стрелку.
   */
  const ждёмЗарплаты = canEdit && (salariesQuery.isPending || otherQuery.isPending);
  if (
    me.isLoading ||
    bootstrap.isLoading ||
    ordersQuery.isLoading ||
    invoicesQuery.isLoading ||
    ждёмЗарплаты
  ) {
    return <Spinner />;
  }

  /*
   * Отказ загрузки ГОВОРИТ О СЕБЕ — как в «Финансах».
   *
   * Доход месяца здесь считается по тем же заказам, и при обрыве связи раздел
   * так же нарисовал бы нули: доли всех сотрудников ушли бы к минимальной
   * зарплате, будто месяц был убыточным. Показывать это молча нельзя.
   */
  const сбой = ordersQuery.error ?? bootstrap.error ?? invoicesQuery.error;
  if (сбой) {
    return (
      <ErrorState
        error={сбой instanceof Error ? сбой : new Error('Не удалось загрузить сотрудников')}
        onRetry={() => {
          void bootstrap.refetch();
          void ordersQuery.refetch();
          void invoicesQuery.refetch();
        }}
      />
    );
  }

  // Дубль серверной проверки: прямой адрес /employees не должен ничего показать.
  if (!canEdit) {
    return <p className="text-sm text-ink2">Раздел «Сотрудники» доступен только владельцу.</p>;
  }

  const monthKey = monthKeyForOffset(today, offset);


  const rentKop = bootstrap.data?.settings?.rentKop ?? 0;
  const taxPercent = bootstrap.data?.settings?.taxPercent ?? 6;
  const счета = (invoicesQuery.data?.invoices ?? []).map((i) => ({
    date: i.date,
    totalKop: i.items.reduce((сумма, п) => сумма + Math.round(п.priceKop * п.qty), 0),
  }));
  /** Гарантия одна на всех: пока процент даёт меньше, платится она (§4). */
  const minSalaryKop = bootstrap.data?.settings?.minSalaryKop ?? 0;
  /*
    Налог участвует и здесь. Доход в «Сотрудниках» и в «Финансах» обязан быть
    одним числом: доля сотрудника считается от него, и разойдись они — в двух
    разделах вышли бы две разные зарплаты за один месяц.
  */
  const налог = taxForMonth(счета, monthKey, taxPercent).taxKop;
  const current = monthTotals(orders, monthKey, rentKop, налог, прочие(monthKey));
  /*
   * Ряд графика обрезается январём 2026: до него программы не было, и
   * пустые столбики за 2025-й читались бы как месяцы без зарплаты.
   */
  const trend = monthKeysUpTo(monthKey, span).map((ключ) => {
    return monthTotals(
      orders,
      ключ,
      rentKop,
      taxForMonth(счета, ключ, taxPercent).taxKop,
      прочие(ключ),
    );
  });

  /*
   * Владелец в расчёте зарплат не участвует: его деньги — остаток. Он
   * остаётся в списке учётных записей ниже, где правится имя и пароль.
   */
  const paid = employees.filter(
    (e) => e.role !== 'OWNER' && e.percent !== undefined && e.bonusKop !== undefined,
  );
  /*
   * Цвет сотрудника — ЕГО, а не место в списке.
   *
   * Раньше цвет выдавался по порядку: первому серый, второму золото. Новый
   * человек, вставший по алфавиту раньше, перекрашивал всех, и сверять
   * месяцы на графике выплат по цвету становилось нельзя. Теперь цвет
   * закреплён в настройках и меняется только в «Настройки → Цвета».
   */
  const цветСотрудника = employeeColors(
    paid.map((e) => e.id),
    bootstrap.data?.settings?.colors,
  );
  /**
   * Фактические выплаты по ВСЕМ месяцам: месяц → сотрудник → сумма.
   *
   * Нужны графику: он показывает восемь месяцев сразу, и по каждому надо
   * знать, платили ли по расчёту или по договорённости.
   */
  const manualByMonth = new Map<string, Map<string, ManualSalary>>();
  for (const запись of salariesQuery.data?.salaries ?? []) {
    const месяц = manualByMonth.get(запись.monthKey) ?? new Map<string, ManualSalary>();
    месяц.set(запись.employeeId, {
      amountKop: запись.amountKop,
      advanceKop: запись.advanceKop,
      vacationKop: запись.vacationKop,
        bonusKop: запись.bonusKop,
      note: запись.note,
    });
    manualByMonth.set(запись.monthKey, месяц);
  }


  /*
   * ЧИСЛА КАБИНЕТА: показанный месяц и полгода столбиками.
   *
   * Считаются здесь же, а не запросом к серверу: у владельца на руках и
   * заказы, и счета, и записи выплат — те самые, из которых сложены карточки
   * выше. Сервер вернул бы ровно это же, но потребовал бы пароль сотрудника.
   *
   * Без useMemo: расчёт идёт только при открытом окне, зато всегда по
   * свежим правкам — ставку двигают в карточке рядом, и кабинет обязан
   * меняться вместе с ней.
   */
  const кабинетСотрудника = (() => {
    if (!кабинет) return null;
    const employee = paid.find((e) => e.id === кабинет);
    if (!employee) return null;

    const считать = (месяц: string) =>
      salaryOf(
        { id: employee.id, name: employee.name, percent: employee.percent!, bonusKop: employee.bonusKop! },
        monthTotals(
          orders,
          месяц,
          rentKop,
          taxForMonth(счета, месяц, taxPercent).taxKop,
          прочие(месяц),
        ).salaryBaseKop,
        minSalaryKop,
        manualByMonth.get(месяц)?.get(employee.id),
        месяц,
      );

    const ключ = monthKeyForOffset(today, кабинетСмещение);
    const строка = считать(ключ);
    /* Весь текущий год: ряд не должен ехать за выбранным месяцем. */
    const история = monthKeysOfYear(today).map((месяц) => ({
      monthKey: месяц,
      salaryKop: считать(месяц).salaryKop,
    }));

    return {
      employee,
      monthKey: ключ,
      открыт: employee.salaryAccess ?? false,
      /* Ровно то же, что отдаёт `POST /api/my-salary`, поле в поле. */
      salary: {
        monthKey: ключ,
        name: employee.name,
        percent: строка.percent,
        shareKop: строка.shareKop,
        minSalaryKop: строка.minSalaryKop,
        atMinimum: строка.atMinimum,
        bonusKop: строка.bonusKop,
        vacationKop: строка.vacationKop,
        advanceKop: строка.advanceKop,
        salaryKop: строка.salaryKop,
        remainderKop: строка.remainderKop,
        manual: строка.manual,
        manualNote: строка.manualNote,
        computedKop: строка.computedKop,
        history: история,
      },
    };
  })();

  /** Записи ЭТОГО месяца: ключ — сотрудник. */
  const manualThisMonth = new Map<string, ManualSalary>();
  for (const запись of salariesQuery.data?.salaries ?? []) {
    if (запись.monthKey === monthKey) {
      manualThisMonth.set(запись.employeeId, {
        amountKop: запись.amountKop,
        advanceKop: запись.advanceKop,
        vacationKop: запись.vacationKop,
        bonusKop: запись.bonusKop,
        note: запись.note,
      });
    }
  }

  /*
   * Доли считаются от БАЗЫ (без налога), а остаток владельца — от дохода
   * после налога. Это разные числа и разные основания: налог платит
   * лаборатория, а не техник.
   */
  /*
   * Есть ли среди накопленных правок хоть одна НАСТОЯЩАЯ.
   *
   * Запись могла появиться и без изменения — например, значение набрали
   * заново тем же числом. Кнопке «Сохранить» полагается гореть тогда, когда
   * есть что сохранять, а не когда в поле просто побывали.
   */
  const salaryDirty = pendingSalary.size > 0;

  for (const [key, value] of pendingSalary) {
    const [salaryMonth, employeeId] = key.split('|');
    if (salaryMonth === monthKey && employeeId) manualThisMonth.set(employeeId, value);
  }

  const salaries = payroll(
    paid.map((e) => ({ id: e.id, name: e.name, percent: e.percent!, bonusKop: e.bonusKop! })),
    current.salaryBaseKop,
    minSalaryKop,
    manualThisMonth,
    current.incomeKop,
    monthKey,
  );

  const accountEmployee = accountFor ? employees.find((e) => e.id === accountFor) : undefined;

  /** Сохранённые на сервере ставки — чтобы показывать «было → станет». */
  const savedById = new Map(
    (bootstrap.data?.employees ?? []).map((e) => [e.id, e as unknown as EmployeeRow]),
  );

  return (
    <div>
      <SectionHeader
        icon={Users}
        title="Сотрудники"
        subtitle={`Зарплаты за ${formatMonthKey(monthKey).toLowerCase()}`}
        right={
          <>
            {/*
              Переключатель месяца и «Добавить» ОТДЕЛЕНЫ чертой от сохранения —
              как в «Заказах», «Врачах» и «Конструкциях». Это работа со
              списком: посмотреть другой месяц, завести сотрудника. «Сохранить»
              и «Отменить» относятся ко всему разделу разом.
            */}
            <span
              className="flex flex-wrap items-center gap-2 md:mr-2 md:border-r md:pr-4"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              {offset !== 0 && (
                <button
                  onClick={() => setOffset(0)}
                  className="rounded-lg border px-2.5 py-1.5 text-xs text-ink2 hover:bg-black/5"
                  style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
                >
                  Текущий месяц
                </button>
              )}
              {/* Левее января 2026 данных нет — и листать туда некуда. */}
              <MonthSwitcher
                label={formatMonthKey(monthKey)}
                onPrev={() => setOffset((v) => v - 1)}
                onNext={() => setOffset((v) => v + 1)}
                prevDisabled={isFirstMonth(monthKey)}
              />
              {/* Основная кнопка, как «Добавить» во «Врачах» и «Клиниках»: это
                  главное действие раздела, и выглядеть оно должно одинаково. */}
              <Button
                onClick={() =>
                  useDrafts.getState().create('employees', 'employee', {
                    name: 'Новый сотрудник', position: 'Техник', login: '',
                    percent: 0, bonusKop: 0, blocked: true,
                  })
                }
              >
                <Plus size={14} /> Добавить
              </Button>
              {/* Масштаб — рядом с выбором месяца: оба про то, ЧТО и КАК
                  видно в рабочей области, а не про правку записей. */}
              <ZoomControl zoom={zoom} hint="Мельче — на экран влезет больше сотрудников" />
            </span>
            <SaveDiscard
              dirty={module.dirty || salaryDirty}
              saving={module.saving || salaryBusy}
              onSave={() => void save()}
              onDiscard={() => {
                module.discard();
                setPendingSalary(new Map());
                show('Изменения отменены');
              }}
              what="Сотрудники"
            />
          </>
        }
      />

      {/*
        РАБОЧАЯ ОБЛАСТЬ раздела — под масштабом.

        Плитки, карточки зарплат, график и таблица сотрудников уменьшаются
        и увеличиваются вместе: это одно рабочее полотно. Шапка с выбором
        месяца, «Добавить» и «Сохранить» остаётся снаружи и прежнего размера —
        до кнопок надо попадать пальцем при любом масштабе.
      */}
      <div style={{ zoom: `${zoom.zoom}%` }}>
      {/* Из чего делится месяц */}
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        {/*
          Первая плитка — ЗАРПЛАТНАЯ БАЗА, а не доход владельца.

          Раздел про зарплаты, и число над карточками обязано быть тем самым,
          от которого считается доля: выручка за вычетом материалов и аренды.
          Стоял же здесь доход после налога и прочих расходов — он меньше, и
          сходиться с долями в карточках не мог. Владелец сверял и не мог
          понять, почему девять процентов от показанного дают другое число.

          Доход владельца никуда не делся — он в «Финансах», там ему и место.
        */}
        <Tile
          label="Зарплатная база"
          valueKop={current.salaryBaseKop}
          hint={`выручка минус материалы и аренда · ${current.orderCount} заказ(ов)`}
        />
        <Tile
          label="Всего зарплат"
          valueKop={salaries.totalSalariesKop}
          hint={`${paid.length} сотрудник(ов)`}
          color={STATUS_COLORS.urgent}
        />
        <Tile
          label="Остаётся вам"
          valueKop={salaries.ownerRemainderKop}
          hint="доход после налога и расходов минус зарплаты"
          color={salaries.ownerRemainderKop >= 0 ? STATUS_COLORS.done : STATUS_COLORS.danger}
          accent
        />
      </div>

      {/* Карточка на сотрудника: расчёт + ползунок ставки */}
      {paid.length > 0 ? (
        <div className="mb-5 grid gap-3 lg:grid-cols-2">
          {paid.map((employee, index) => (
            <SalaryCard
              key={`${employee.id}:${monthKey}`}
              onКабинет={() => {
                setКабинетСмещение(offset);
                setКабинет(employee.id);
              }}
              employee={employee}
              /* Тот же цвет, что у столбика этого сотрудника на графике ниже. */
              color={цветСотрудника.get(employee.id)!}
              savedPercent={savedById.get(employee.id)?.percent}
              incomeKop={current.salaryBaseKop}
              minSalaryKop={minSalaryKop}
              onPercent={(value) => update(employee.id, { percent: value })}
              onBonus={(value) => правитьЗапись(employee.id, { bonusKop: Math.max(0, value) })}
              manual={записьМесяца(employee.id)}
              monthKey={monthKey}
              monthLabel={formatMonthKey(monthKey).toLowerCase()}
              busy={salaryBusy || employee.id.startsWith('tmp_')}
              onAdvance={(kop) =>
                правитьЗапись(employee.id, { advanceKop: Math.max(0, kop) })
              }
              onVacation={(kop) =>
                правитьЗапись(employee.id, { vacationKop: Math.max(0, kop) })
              }
            />
          ))}
        </div>
      ) : (
        <p
          className="mb-5 rounded-xl border border-dashed px-4 py-6 text-center text-sm text-ink2"
          style={{ borderColor: 'var(--otc-line)' }}
        >
          Сотрудников со ставкой нет — делить доход не с кем.
        </p>
      )}

      {/* График выплат по месяцам */}
      {paid.length > 0 && (
        <div
          className="mb-5 rounded-xl border p-4 sm:p-5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-ink2">
              Выплаты по месяцам
            </p>
            <div className="flex gap-1">
              {[3, 6, 12].map((value) => (
                <button
                  key={value}
                  onClick={() => setSpan(value)}
                  className="rounded-md border px-2 py-1 text-xs"
                  style={{
                    borderColor: span === value ? 'var(--otc-accent)' : 'var(--otc-line)',
                    color: span === value ? '#fff' : 'var(--otc-ink2)',
                    background: span === value ? 'var(--otc-accent)' : 'transparent',
                  }}
                >
                  {value} мес.
                </button>
              ))}
            </div>
          </div>

          {/*
            Столбики СЛОЖЕНЫ в один: важна не зарплата каждого по отдельности,
            а сколько месяц забирает фонд оплаты целиком.

            Где выплата записана ВРУЧНУЮ — берётся она, а не расчёт. Иначе
            график расходился с карточкой у себя же над ним: в августе за
            Урсеговым записано 84 450, а столбик показывал расчётные 88 285,
            и понять, какому числу верить, было нельзя. За месяцы без записи
            по-прежнему считается по нынешней ставке — как оценка.
          */}
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={trend.map((t) => {
                const row: Record<string, string | number> = {
                  month: formatMonthKey(t.monthKey).split(' ')[0] ?? t.monthKey,
                };
                for (const employee of paid) {
                  row[employee.name] =
                    salaryOf(
                      { id: employee.id, name: employee.name, percent: employee.percent!, bonusKop: employee.bonusKop! },
                      t.salaryBaseKop,
                      minSalaryKop,
                      manualByMonth.get(t.monthKey)?.get(employee.id),
                      t.monthKey,
                    ).salaryKop / 100;
                }
                return row;
              })}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--otc-line)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--otc-ink2)" />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="var(--otc-ink2)"
                tickFormatter={(value: number) =>
                  // Через модуль — иначе убыток подписывался бы полным числом,
                  // как было в «Финансах»: сверху «90к», снизу «−90000».
                  Math.abs(value) >= 1000 ? `${Math.round(value / 1000)}к` : String(value)
                }
              />
              <Tooltip
                formatter={(value: number) => `${value.toLocaleString('ru-RU')} ₽`}
                contentStyle={{
                  borderColor: 'var(--otc-line)',
                  borderRadius: 8,
                  background: 'var(--otc-surface)',
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {paid.map((employee, index) => (
                <Bar
                  key={employee.id}
                  dataKey={employee.name}
                  stackId="salary"
                  fill={цветСотрудника.get(employee.id)}
                  radius={index === paid.length - 1 ? [3, 3, 0, 0] : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Учётные записи — вход в программу, не деньги */}
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink2">Учётные записи</p>

      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: 'var(--otc-line)' }}>
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse', minWidth: 460 }}>
          <thead>
            <tr style={{ background: 'var(--otc-table-head)' }}>
              {/*
                Логин и пароль на телефоне СКРЫТЫ.

                Здесь они только показываются — правятся в окне «Доступ», —
                а места занимают столько, что таблица переставала помещаться
                в экран и уезжала вбок целиком, вместе с фамилиями. Видеть,
                кто есть кто и открыт ли вход, важнее, чем видеть строку
                логина, которую всё равно нельзя тронуть.
              */}
              {[
                { label: 'ФИО', hideOnPhone: false },
                { label: 'Должность', hideOnPhone: false },
                { label: 'Логин', hideOnPhone: true },
                { label: 'Пароль', hideOnPhone: true },
                { label: 'Вход', hideOnPhone: false },
                { label: '', hideOnPhone: false },
                { label: '', hideOnPhone: false },
              ].map((header, index) => (
                <th
                  key={index}
                  // Шапка столбца с фамилией приклеена вместе с ним же в строках.
                  className={`whitespace-nowrap px-3 py-2 text-left text-xs font-semibold text-white${
                    header.hideOnPhone ? ' hidden md:table-cell' : ''
                  }${index === 0 ? ' sticky left-0 z-10' : ''}`}
                  style={index === 0 ? { background: 'var(--otc-table-head)' } : undefined}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((employee, index) => {
              const isOwner = employee.role === 'OWNER';
              return (
                /*
                  Фон и подсветка — теми же классами, что в журнале заказов:
                  один вид таблицы во всей программе. Правила в index.css.
                */
                <tr
                  key={employee.id}
                  className={['otc-row', index % 2 ? 'otc-row-alt' : ''].filter(Boolean).join(' ')}
                  style={{ borderTop: '1px solid var(--otc-line)' }}
                >
                  <td
                    className="sticky left-0 z-10 px-3 py-2.5"
                    /*
                     * Заливка НАСЛЕДУЕТСЯ у строки, а не задаётся своим цветом.
                     *
                     * Приклеенная ячейка едет поверх соседних, и без
                     * непрозрачного фона сквозь неё просвечивали бы уезжающие
                     * столбцы. Строки чередуются по цвету и подсвечиваются при
                     * наведении — `inherit` берёт нужный оттенок сам, а
                     * прописанный цвет пришлось бы держать в согласии с
                     * правилами `.otc-row` в index.css.
                     */
                    style={{ background: 'inherit' }}
                  >
                    <EditableText
                      value={employee.name}
                      disabled={!canEdit}
                      onSave={(value) => update(employee.id, { name: String(value) })}
                      textStyle={{ color: 'var(--otc-ink)' }}
                    />
                    {isOwner && <span className="ml-1 text-[10px] text-ink2">владелец</span>}
                  </td>

                  <td className="px-3 py-2.5 text-xs">
                    {canEdit && !isOwner ? (
                      <select
                        className="rounded border px-1.5 py-1 text-xs"
                        style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-ink)', background: 'transparent' }}
                        value={employee.position}
                        onChange={(e) => update(employee.id, { position: e.target.value })}
                      >
                        <option>Техник</option>
                        <option>Администратор</option>
                      </select>
                    ) : (
                      <span className="text-ink2">{employee.position}</span>
                    )}
                  </td>

                  {/*
                    Логин, пароль и вход здесь только ПОКАЗЫВАЮТСЯ. Правятся они
                    в окне «Доступ»: пароль проверяется на длину и уходит на
                    сервер отдельным запросом, а не общим пакетом изменений —
                    в ячейке таблицы этому места нет.
                  */}
                  <td className="hidden px-3 py-2.5 text-xs md:table-cell">
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: 'var(--otc-ink2)' }}>
                      {employee.login || <span className="italic">не задан</span>}
                    </span>
                  </td>

                  <td className="hidden px-3 py-2.5 text-xs md:table-cell">
                    {employee.hasPassword ? (
                      <span className="text-ink2">задан</span>
                    ) : (
                      <span style={{ color: STATUS_COLORS.warning }}>не задан</span>
                    )}
                  </td>

                  <td className="px-3 py-2.5">
                    <span
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
                      style={{
                        background: employee.blocked ? 'rgba(192,69,60,0.1)' : 'rgba(75,122,90,0.1)',
                        color: employee.blocked ? STATUS_COLORS.danger : STATUS_COLORS.done,
                      }}
                    >
                      {employee.blocked ? <Lock size={11} /> : <Unlock size={11} />}
                      {employee.blocked ? 'Закрыт' : 'Открыт'}
                    </span>
                  </td>

                  <td className="px-3 py-2.5">
                    <button
                      disabled={!canEdit}
                      onClick={() => setAccountFor(employee.id)}
                      className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs text-ink2 hover:bg-black/5 disabled:opacity-40"
                      style={{ borderColor: 'var(--otc-line)' }}
                      title="Логин, пароль, разрешение на вход"
                    >
                      <KeyRound size={13} /> Доступ
                    </button>
                  </td>

                  <td className="px-3 py-2.5">
                    {canEdit &&
                      !isOwner &&
                      (confirmDelete === employee.id ? (
                        <span className="inline-flex items-center gap-1">
                          <button
                            onClick={() => {
                              useDrafts.getState().remove('employees', 'employee', employee.id);
                              setConfirmDelete(null);
                            }}
                            className="rounded-md px-2 py-1 text-xs text-white"
                            style={{ background: 'var(--otc-coral)' }}
                          >
                            Удалить
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="rounded-md border px-2 py-1 text-xs text-ink2"
                            style={{ borderColor: 'var(--otc-line)' }}
                          >
                            Отмена
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(employee.id)}
                          className="rounded-md p-1.5 hover:bg-black/5"
                          title="Удалить сотрудника"
                        >
                          <Trash2 size={13} style={{ color: 'var(--otc-coral)' }} />
                        </button>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>

      {accountEmployee && (
        <AccountModal
          employee={accountEmployee}
          onField={(patch) => update(accountEmployee.id, patch)}
          onSetPassword={(value) => setEmployeePassword(accountEmployee.id, accountEmployee.name, value)}
          onClose={() => setAccountFor(null)}
        />
      )}

      {module.conflicts && (
        <ConflictModal
          conflicts={module.conflicts}
          onReload={() => {
            module.discard();
            module.dismissConflicts();
            void bootstrap.refetch();
          }}
          onKeepMine={() => void module.keepMine()}
          onClose={module.dismissConflicts}
        />
      )}

      {/*
        КАБИНЕТ СОТРУДНИКА — ровно тот экран, который увидит человек.

        Рисуется тем же кусочком, что и раздел «Моя зарплата», поэтому
        разойтись им негде. Числа считаются здесь же, на стороне владельца:
        сервер для этого не нужен, а пароль тем более — владелец видит эти
        деньги строкой выше.
      */}
      {кабинетСотрудника && (
        <CardWindow
          ключ="otc.cabinetWindow"
          title={`${кабинетСотрудника.employee.name} — кабинет сотрудника`}
          ширина={760}
          onClose={() => setКабинет(null)}
          footer={
            <Button variant="secondary" onClick={() => setКабинет(null)}>
              Закрыть
            </Button>
          }
        >
          <ВидЗарплаты
            salary={кабинетСотрудника.salary}
            monthKey={кабинетСотрудника.monthKey}
            /* Столбики — цветом этого сотрудника, как на общем графике выплат. */
            цвет={цветСотрудника.get(кабинетСотрудника.employee.id)}
            onPrev={() => setКабинетСмещение((о) => о - 1)}
            onNext={() => setКабинетСмещение((о) => о + 1)}
            nextDisabled={кабинетСмещение >= 0}
            onМесяц={(ключ) => setКабинетСмещение(смещениеМесяца(today, ключ))}
          />
        </CardWindow>
      )}

      <Toast toast={toast} />
    </div>
  );
}

/** Плитка сводки месяца. */
function Tile({
  label,
  valueKop,
  hint,
  color,
  accent,
}: {
  label: string;
  valueKop: number;
  hint: string;
  color?: string;
  accent?: boolean;
}) {
  return (
    <div
      className="rounded-xl border p-4"
      style={{
        borderColor: accent ? 'var(--otc-accent)' : 'var(--otc-line)',
        background: 'var(--otc-surface)',
      }}
    >
      <p className="text-xs text-ink2">{label}</p>
      <p className="tabular mt-1 text-xl font-semibold sm:text-2xl" style={{ color: color ?? 'var(--otc-ink)' }}>
        {formatKop(valueKop)} ₽
      </p>
      <p className="mt-0.5 text-xs text-ink2">{hint}</p>
    </div>
  );
}

/**
 * Окно «Доступ» — всё про вход сотрудника в программу в одном месте.
 *
 * Раньше логин правился прямо в ячейке таблицы, пароль задавался всплывающей
 * строчкой рядом, а разрешение на вход переключалось третьей кнопкой. Три
 * органа управления в одной строке, и ни один не объяснял, что делает.
 *
 * Пароль живёт ОТДЕЛЬНО от общего пакета изменений: он уходит на сервер сразу
 * и обратно не отменяется. Логин и разрешение на вход — обычные поля, они
 * сохраняются кнопкой «Сохранить изменения» вместе со всем остальным. Разница
 * названа в окне прямым текстом, иначе владелец решит, что пароль тоже ждёт
 * сохранения, и уйдёт со страницы, не применив его.
 */
function AccountModal({
  employee,
  onField,
  onSetPassword,
  onClose,
}: {
  employee: EmployeeRow;
  onField: (patch: Record<string, unknown>) => void;
  onSetPassword: (password: string) => Promise<void>;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  const isOwner = employee.role === 'OWNER';
  const isNew = employee.version === 0;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;

  const apply = async () => {
    setSaving(true);
    try {
      await onSetPassword(password);
      setPassword('');
      setVisible(false);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Доступ — ${employee.name}`}
      onClose={onClose}
      size="sm"
      /*
       * Кнопки — В ПОДВАЛЕ окна, а не последним блоком содержимого.
       *
       * Раньше они стояли внутри тела: «Закрыть» висела в левом нижнем углу
       * вплотную к краю, без линии и заливки, и читалась как оброненная, а не
       * как завершение окна. У всех прочих окон программы кнопки в подвале —
       * это же выбивалось из общего строя.
       *
       * Кнопка пароля появляется, КОГДА ЕСТЬ ЧТО ПРИМЕНЯТЬ. Пустое поле —
       * обычное состояние: сюда заходят и просто посмотреть логин или закрыть
       * вход. Кнопка при этом стояла серой, и окно выглядело сломанным —
       * главное действие как будто не работает.
       */
      footer={
        <>
          {password.length > 0 && (
            <Button onClick={() => void apply()} disabled={saving || password.length < MIN_PASSWORD}>
              <Check size={14} /> {saving ? 'Применение…' : 'Применить пароль'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/*
          ДОСТУП К СВОЕЙ ЗАРПЛАТЕ — признак сотрудника, а не следствие
          должности. Включён — у человека появляется раздел «Моя зарплата»,
          где он видит свои деньги и их расчёт, но только свои и только
          после ввода пароля. Выключен — раздела нет, и запрос отвечает
          отказом, даже если адрес набрать руками.
        */}
        {!isOwner && (
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={employee.salaryAccess ?? false}
              onChange={() => onField({ salaryAccess: !employee.salaryAccess })}
            />
            <span className="min-w-0">
              <span className="block text-sm text-ink">Видит свою зарплату</span>
              <span className="text-[11px] text-ink2">
                Свой расчёт за месяц: доля, премия, аванс, остаток. Чужие зарплаты и
                финансы лаборатории остаются закрытыми.
              </span>
            </span>
          </label>
        )}
        {/*
          Логин МЕНЯЕТСЯ и у существующей записи.
          
          Сессии и журнал изменений ссылаются на сотрудника по внутреннему
          номеру, а не по логину, — переименование ничего не рвёт. Под старым
          логином войти перестанут, под новым начнут; открытые сессии
          продолжают работать, потому что держатся на том же номере.
          
          Сохраняется общей кнопкой «Сохранить», как имя и должность.
        */}
        <Field
          label="Логин"
          hint={
            isNew
              ? 'Латиницей, без пробелов'
              : 'Под старым логином войти будет нельзя. Открытые сессии не оборвутся'
          }
        >
          <input
            className="otc-input"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            value={employee.login}
            placeholder="ivanov"
            onChange={(e) => onField({ login: e.target.value.trim() })}
          />
        </Field>

        <Field
          label={employee.hasPassword ? 'Новый пароль' : 'Пароль'}
          hint={
            isNew
              ? 'Сначала сохраните сотрудника — пароль задаётся уже существующей записи'
              : `Не короче ${MIN_PASSWORD} символов. Применяется сразу, отдельно от «Сохранить»`
          }
        >
          <div className="flex gap-1.5">
            <input
              className="otc-input"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              type={visible ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              disabled={isNew}
              placeholder={employee.hasPassword ? 'оставьте пустым, чтобы не менять' : 'придумайте или сгенерируйте'}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              onClick={() => setVisible((v) => !v)}
              disabled={isNew}
              className="flex-shrink-0 rounded-lg border px-2 hover:bg-black/5 disabled:opacity-40"
              style={{ borderColor: 'var(--otc-line)' }}
              title={visible ? 'Скрыть' : 'Показать'}
            >
              {visible ? <EyeOff size={14} className="text-ink2" /> : <Eye size={14} className="text-ink2" />}
            </button>
          </div>
        </Field>

        {/*
          Пароль ПОКАЗЫВАЕТСЯ владельцу и генерируется здесь же: его надо
          продиктовать сотруднику. Обратно из базы он не читается никогда —
          там лежит только хеш, — поэтому единственная возможность увидеть
          пароль целиком именно эта, до применения.
        */}
        {/*
          Кнопка, а не подчёркнутая ссылка: во всей программе действия —
          кнопки, и ссылка среди них читалась как случайно попавший текст.
        */}
        <button
          onClick={() => {
            setPassword(randomPassword());
            setVisible(true);
          }}
          disabled={isNew}
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium text-ink2 transition-colors hover:bg-black/5 disabled:opacity-40"
          style={{ borderColor: 'var(--otc-line)' }}
        >
          <KeyRound size={13} /> Придумать пароль
        </button>

        {tooShort && (
          <p className="text-xs" style={{ color: STATUS_COLORS.danger }}>
            Слишком короткий — нужно минимум {MIN_PASSWORD} символов
          </p>
        )}

        <Field
          label="Вход в программу"
          hint={
            isOwner
              ? 'Владельца заблокировать нельзя — иначе в программу не войдёт никто'
              : 'Закрытый доступ обрывает и уже открытые сессии'
          }
        >
          {/*
            Состояние и действие РАЗДЕЛЕНЫ.

            Раньше на одной кнопке стояло и то, и другое — «Открыт, нажмите,
            чтобы закрыть», — и с одного взгляда было не понять, открыт вход
            сейчас или его предлагают открыть. Слева состояние, справа
            кнопка с тем, что произойдёт.
          */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium"
              style={{
                background: employee.blocked
                  ? 'color-mix(in srgb, var(--otc-coral) 12%, transparent)'
                  : 'color-mix(in srgb, var(--otc-sage) 14%, transparent)',
                color: employee.blocked ? STATUS_COLORS.danger : STATUS_COLORS.done,
              }}
            >
              {employee.blocked ? <Lock size={14} /> : <Unlock size={14} />}
              {employee.blocked ? 'Вход закрыт' : 'Вход открыт'}
            </span>

            {!isOwner && (
              <button
                onClick={() => onField({ blocked: !employee.blocked })}
                className="rounded-lg border px-3 py-1.5 text-sm text-ink2 transition-colors hover:bg-black/5"
                style={{ borderColor: 'var(--otc-line)' }}
              >
                {employee.blocked ? 'Открыть вход' : 'Закрыть вход'}
              </button>
            )}
          </div>
        </Field>
      </div>

    </Modal>
  );
}

/** Минимальная длина пароля — та же, что проверяет сервер. */
const MIN_PASSWORD = 8;

/**
 * Случайный пароль.
 *
 * `crypto.getRandomValues`, а не `Math.random`: последний предсказуем, и
 * пароль, сгенерированный из него, ничем не лучше даты рождения. Похожие
 * символы (0/O, 1/l/I) исключены — пароль диктуют голосом.
 */
function randomPassword(length = 12): string {
  const ALPHABET = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => ALPHABET[value % ALPHABET.length]).join('');
}

/**
 * Карточка зарплаты: расчёт за месяц и правка ставки ползунком.
 *
 * Ползунок нужен не вместо поля ввода, а вместе с ним: цифру набирают, когда
 * знают её заранее, а ползунком ПОДБИРАЮТ — и тогда важно видеть, во что
 * обходится каждый шаг. Строка «было → станет» показывает разницу до
 * сохранения, пока решение ещё можно передумать.
 */
/**
 * Значение, которое правится ПРЯМО В ПОЛЕ — без карандаша и «Изменить».
 *
 * Раньше каждое число в карточке пряталось за значком: чтобы поменять
 * премию или ставку, надо было сначала попасть пальцем в карандаш размером
 * в десяток точек, и только потом набрать число. Действие одно — правка, —
 * а шагов было два, причём первый ничего не делал.
 *
 * Поле ТЕКСТОВОЕ, а не числовое: ставка бывает дробной («22,5 %»), а
 * числовое поле не даёт набрать запятую — русская раскладка ставит именно
 * её, и ввод обрывался на первом же символе после числа.
 *
 * Пока в поле печатают, оно показывает НАБРАННОЕ, а не разобранное: иначе
 * промежуточное «22,» тут же переписывалось бы обратно в «22».
 */
function ValueField({
  text,
  onCommit,
  suffix,
  width = 84,
  live = false,
  disabled = false,
  placeholder,
  accent,
}: {
  /** Значение, как оно выглядит вне правки: «20 000», «9». */
  text: string;
  onCommit: (raw: string) => void;
  suffix?: string;
  width?: number;
  /**
   * Сообщать о каждом нажатии клавиши, а не только по окончании.
   *
   * Нужно ставке: рядом с полем ползунок и подсказка «при доходе… это…»,
   * и они обязаны двигаться вместе с набираемым числом. Деньгам это
   * вредно — каждая цифра уходила бы на сервер отдельным запросом.
   */
  live?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Цвет рамки в правке — цвет сотрудника. */
  accent?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const editing = draft !== null;

  return (
    <span
      className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border px-2 py-1"
      style={{
        borderColor: editing ? (accent ?? 'var(--otc-accent)') : 'var(--otc-line)',
        background: disabled ? 'var(--otc-bg)' : 'var(--otc-surface)',
      }}
    >
      <input
        type="text"
        inputMode="decimal"
        disabled={disabled}
        placeholder={placeholder}
        value={draft ?? text}
        style={{ width }}
        className="tabular bg-transparent text-right text-sm text-ink outline-none"
        onFocus={(e) => {
          setDraft(text);
          // Набранное заменяет прежнее: обычно значение меняют целиком.
          e.currentTarget.select();
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          if (live) onCommit(e.target.value);
        }}
        onBlur={() => {
          /*
           * Нетронутое значение НЕ записывается.
           *
           * Иначе достаточно было ткнуть в поле и уйти, чтобы раздел счёл
           * это правкой и зажёг «Сохранить»: человек ничего не менял, а
           * программа уверяет, что менял.
           */
          if (draft !== null && !live && draft !== text) onCommit(draft);
          setDraft(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(null);
            e.currentTarget.blur();
          }
        }}
      />
      {suffix && <span className="flex-shrink-0 text-sm text-ink2">{suffix}</span>}
    </span>
  );
}

function SalaryCard({
  employee,
  color,
  savedPercent,
  incomeKop,
  minSalaryKop,
  onPercent,
  onBonus,
  manual,
  monthKey,
  monthLabel,
  busy,
  onКабинет,
  onAdvance,
  onVacation,
}: {
  employee: EmployeeRow;
  /** Цвет сотрудника — общий для ползунка, точки и столбика на графике. */
  color: string;
  savedPercent: number | undefined;
  incomeKop: number;
  /**
   * От чего считается доля: выручка − себестоимость − аренда, БЕЗ налога.
   *
   * Налог сюда не входит намеренно: он обязательство владельца, а не
   * условие оплаты труда.
   */
  /** Гарантия, ниже которой оплата не опускается, копейки. */
  minSalaryKop: number;
  onPercent: (value: number) => void;
  onBonus: (value: number) => void;
  /** Запись месяца: назначенная сумма и выданный аванс, если они есть. */
  manual?: ManualSalary;
  /** Ключ открытого месяца: по нему видно, был ли тогда расчёт по доле. */
  monthKey: string;
  monthLabel: string;
  busy: boolean;
  /** Открыть кабинет сотрудника — тот же экран, что видит он сам. */
  onКабинет: () => void;
  /** Записать аванс за открытый месяц, копейки. */
  onAdvance: (kop: number) => void;
  /** Записать отпускные за открытый месяц, копейки. */
  onVacation: (kop: number) => void;
}) {


  const percent = employee.percent ?? 0;
  const bonusKop = manual?.bonusKop ?? 0;
  const line = salaryOf(
    { id: employee.id, name: employee.name, percent, bonusKop },
    incomeKop,
    minSalaryKop,
    manual,
    monthKey,
  );

  /*
   * ПРЕЖНИЙ ПОРЯДОК: до сентября 2026 доли не было.
   *
   * Тогда платили сдельно, по таблицам учёта работ, и сумму вносили в
   * программу руками. Ставка у сотрудника записана нынешняя, и приложи её к
   * августу — выйдет число, которого никто не платил. Поэтому за такие
   * месяцы карточка показывает выплату и больше ничего: ни доли, ни
   * гарантии, ни премии, ни ползунка ставки. Править там нечего — месяц
   * закрыт, деньги отданы.
   */
  const доля = shareApplies(monthKey);

  const changed = savedPercent !== undefined && savedPercent !== percent;
  const wasKop = changed
    ? salaryOf(
        { id: employee.id, name: employee.name, percent: savedPercent, bonusKop },
        incomeKop,
        minSalaryKop,
        undefined,
        monthKey,
      ).salaryKop
    : 0;
  const deltaKop = line.salaryKop - wasKop;

  // Шкала подстраивается под ставку: обычно это 0–50 %, но и 70 % не сломает.
  const max = Math.max(50, Math.ceil(percent / 10) * 10);


  return (
    <div
      className="rounded-xl border p-4"
      style={{
        borderColor: changed ? STATUS_COLORS.warning : 'var(--otc-line)',
        background: 'var(--otc-surface)',
      }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-baseline gap-2">
          <span
            className="mt-1 inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
            style={{ background: color }}
          />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-ink">{employee.name}</span>
            <span className="text-xs text-ink2">{employee.position}</span>
          </span>
        </span>
        {/*
          Напротив фамилии — ЗАРПЛАТА ЗА МЕСЯЦ целиком.

          Это итоговая величина месяца: сколько человек заработал. Сколько
          осталось отдать на руки — вопрос следующий, и он стоит строкой
          ниже: аванс уже выдан, но от месячного заработка он не отнимается,
          он его часть.
        */}
        <span className="tabular flex-shrink-0 text-right">
          <span className="block text-lg font-semibold text-ink">
            {formatKop(line.salaryKop)} ₽
          </span>
          <span className="text-xs text-ink2">итого за месяц</span>
        </span>
      </div>

      {/*
        ВЫПЛАТА ВРУЧНУЮ — обычным полем, как премия и аванс рядом.

        Раньше здесь была ссылка с карандашом, открывавшая форму с тремя
        полями и тремя кнопками: чтобы записать одно число, приходилось
        открыть, набрать и нажать «Записать». Теперь число набирается прямо
        здесь, а пустое поле означает «платим по расчёту» — и подпись это
        говорит словами.

        Сама возможность — поверх всех правил.

        Процент, гарантия и премия описывают, как платить дальше. За закрытые
        месяцы платили по-другому: технику — сдельно, за печать и рисование, по
        своим таблицам, — и расчёт этих сумм не воспроизводит. Здесь владелец
        ставит выплату прямо: она заменяет расчёт целиком, а расчёт остаётся
        подписью рядом, чтобы видеть разницу.

        Сумма привязана к ОТКРЫТОМУ месяцу: перелистнув стрелками на июль,
        ставят июльскую. Поэтому месяц назван в подписи — иначе легко записать
        не туда.
      */}
      {/*
        ОСТАТОК К ВЫПЛАТЕ — сколько ещё отдать на руки.

        Стоит сразу под заработком месяца, потому что это и есть действие:
        зарплата — величина расчётная, а остаток берут из кассы. Считается
        он вычитанием аванса, и подпись прямо называет вычтенное, чтобы не
        сверять числа в уме.
      */}
      <div
        className="mb-3 flex items-center justify-between gap-3 rounded-lg border p-3"
        style={{
          borderColor:
            line.remainderKop < 0 ? STATUS_COLORS.warning : 'var(--otc-line)',
          background: 'var(--otc-bg)',
        }}
      >
        <span className="min-w-0">
          {/*
            «Остаток к выплате» — про будущее: столько ещё предстоит отдать.
            В закрытом месяце отдавать нечего, деньги давно у человека, и
            то же число там называется тем, что оно есть: выплачено.
          */}
          <span className="block text-xs text-ink2">
            {доля ? 'Остаток к выплате' : 'Выплачено'}
          </span>
          <span className="text-[11px] text-ink2">
            {!доля
              ? 'записано вручную — так платили до сентября 2026'
              : line.advanceKop > 0
                ? `зарплата минус аванс ${formatKop(line.advanceKop)} ₽`
                : 'аванса не было — платим целиком'}
          </span>
        </span>
        <span
          className="tabular flex-shrink-0 text-base font-semibold"
          style={{
            color: line.remainderKop < 0 ? STATUS_COLORS.warning : 'var(--otc-ink)',
          }}
        >
          {formatKop(line.remainderKop)} ₽
        </span>
      </div>
      {/*
        «Кабинет сотрудника» — посмотреть его экран своими глазами.

        Стоит сразу под остатком: выше — взгляд владельца (ползунок ставки,
        премия, аванс), а по кнопке открывается ровно то, что видит сам
        человек, теми же словами и в том же порядке. Доступ при этом не
        выдаётся: галочка «Видит свою зарплату» живёт отдельно, ниже.
      */}
      {/*
        Вид — как у «Добавить»: та же сплошная кнопка цветом темы. Обведённая
        белая терялась среди строк расчёта, хотя открывает она целый экран.
      */}
      <div className="mb-3">
        <Button onClick={onКабинет}>
          <Eye size={14} /> Кабинет сотрудника
        </Button>
      </div>
      {/*
        Разбивка: итог не выводится ни из одной строки по отдельности. Доля
        может вообще не участвовать — если не дотянула до гарантии, платится
        гарантия; премия же начисляется всегда, даже при убыточном месяце.
        Поэтому строка, которая не пошла в оплату, зачёркивается, а не
        прячется: владелец должен видеть, НАСКОЛЬКО процент не дотянул.
      */}
      <div className="mb-3 space-y-1 text-xs">
        {line.manual && доля && (
          <>
            {/*
              Пояснение к ручной сумме — ДВУМЯ строками, а не одной.

              Подпись «по расчёту вышло бы» вместе с припиской вроде «по
              таблице учёта печати и рисования» не помещалась в половину
              карточки и налезала на число справа. Число обязано стоять у
              правого края и быть читаемым — это единственное, ради чего
              строку и смотрят; пояснение уходит под неё отдельной строкой,
              где ему хватает всей ширины.
            */}
            <div className="flex justify-between gap-3">
              <span style={{ color: STATUS_COLORS.warning }}>по расчёту вышло бы</span>
              <span className="tabular flex-shrink-0 font-medium" style={{ color: STATUS_COLORS.warning }}>
                {formatKop(line.computedKop)} ₽
              </span>
            </div>
            {line.manualNote && (
              <p className="text-[11px] leading-snug text-ink2">{line.manualNote}</p>
            )}
          </>
        )}
        {/* Месяц прежнего порядка объясняется одной строкой вместо разбора. */}
        {!доля && (
          <p className="text-[11px] leading-snug text-ink2">
            {line.manualNote
              ? line.manualNote
              : 'сумма за месяц внесена вручную: доля от дохода считается с сентября 2026'}
          </p>
        )}
        {доля && (
          <div className="flex justify-between gap-3">
            <span className={line.atMinimum ? 'text-ink2 line-through opacity-60' : 'text-ink2'}>
              доля {formatPercent(percent)}% от дохода
            </span>
            <span
              className={`tabular flex-shrink-0 font-medium ${line.atMinimum ? 'line-through opacity-60' : ''}`}
              style={{ color }}
            >
              {formatKop(line.shareKop)} ₽
            </span>
          </div>
        )}
        {line.atMinimum && (
          <div className="flex justify-between gap-3">
            <span style={{ color: STATUS_COLORS.warning }}>минимальная зарплата</span>
            <span className="tabular font-medium" style={{ color: STATUS_COLORS.warning }}>
              {formatKop(line.baseKop)} ₽
            </span>
          </div>
        )}
        {(доля || line.bonusKop > 0) && (
          <div className="flex justify-between gap-3">
            <span className="text-ink2">премия</span>
            <span className="tabular text-ink">{formatKop(line.bonusKop)} ₽</span>
          </div>
        )}
        {line.vacationKop > 0 && (
          <div className="flex justify-between gap-3">
            <span className="text-ink2">отпускные</span>
            <span className="tabular text-ink">+{formatKop(line.vacationKop)} ₽</span>
          </div>
        )}
        {line.advanceKop > 0 && (
          <>
            <div className="flex justify-between gap-3">
              <span style={{ color: STATUS_COLORS.warning }}>аванс выдан</span>
              <span
                className="tabular flex-shrink-0 font-medium"
                style={{ color: STATUS_COLORS.warning }}
              >
                −{formatKop(line.advanceKop)} ₽
              </span>
            </div>
            {line.remainderKop < 0 && (
              /* Аванс перекрыл месяц: это переплата, и её видно словами. */
              <p className="text-[11px] leading-snug" style={{ color: STATUS_COLORS.warning }}>
                аванс больше итога — переплата {formatKop(-line.remainderKop)} ₽
              </p>
            )}
          </>
        )}
      </div>

      {/* Ставка и премия — орудия расчёта: в закрытых месяцах их не было. */}
      {доля && (
        <>
        {/* Калькулятор ставки */}
        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs text-ink2">Ставка</span>
            {/*
              Ставка правится СРАЗУ, как и всё остальное в карточке.

              Прежде она пряталась за кнопкой с карандашом, а по нажатию
              появлялись поле, галочка и крестик — четыре органа управления
              там, где нужно одно число. Отменить неудачную правку
              по-прежнему можно: ставка уходит в черновик раздела, и наверху
              есть общая «Отмена» — она вернёт всё, что не сохранили.
            */}
            <ValueField
              text={formatPercent(percent)}
              onCommit={(raw) => {
                const parsed = parsePercent(raw);
                if (parsed !== null) onPercent(parsed);
              }}
              live
              suffix="%"
              width={44}
              accent={color}
            />
          </div>
          {/*
            Шаг ползунка — половина процента: точнее набирается полем выше.

            Цвет задаётся ДВУМЯ переменными, а не одним `accent-color`. Тот красит
            только заполненную часть и бегунок, а цвет пустой дорожки браузер
            выводит сам: у синей ставки она выходила светло-серой, у золотой —
            чёрной. Два ползунка рядом выглядели по-разному, хотя различаться
            должны были только цветом сотрудника.
          */}
          <input
            type="range"
            min={0}
            max={max}
            step={0.5}
            value={Math.min(percent, max)}
            onChange={(e) => onPercent(Number(e.target.value))}
            /*
              На ТЕЛЕФОНЕ ползунок только показывает, но не двигается.

              Касание в его полосу сразу переставляло ставку, а пальцем в неё
              попадают случайно, просто пролистывая список — речь о зарплате,
              и менять её мимоходом нельзя. Мышью такого промаха не бывает,
              поэтому на широком экране он остаётся живым, а набрать точное
              число всегда можно в поле рядом.
            */
            className="otc-range pointer-events-none md:pointer-events-auto"
            style={
              {
                '--otc-fill-color': color,
                '--otc-fill': `${(Math.min(percent, max) / max) * 100}%`,
              } as CSSProperties
            }
          />

          <p className="mt-1.5 text-xs">
            {changed ? (
              <span style={{ color: STATUS_COLORS.warning }}>
                было {formatPercent(savedPercent)}% ({formatKop(wasKop)} ₽) → станет {formatPercent(percent)}%
                ({formatKop(line.salaryKop)} ₽),
                {deltaKop === 0 ? (
                  <> к выплате столько же — гарантия перекрывает обе ставки</>
                ) : (
                  <>
                    {' '}это на <span className="tabular">{formatKop(Math.abs(deltaKop))} ₽</span>{' '}
                    {deltaKop > 0 ? 'больше' : 'меньше'}
                  </>
                )}{' '}
                — не забудьте сохранить
              </span>
            ) : (
              /*
                Подсказка называет долю И итог, когда они расходятся. Одна доля
                вводила бы в заблуждение: владелец двигает ставку, видит число и
                считает его выплатой — а платится гарантия, и до неё изменение
                ставки на выплату не влияет вовсе.
              */
              <span className="text-ink2">
                при доходе до налога <span className="tabular">{formatKop(incomeKop)} ₽</span>{' '}
                это{' '}
                <span className="tabular">{formatKop(line.shareKop)} ₽</span>
                {line.atMinimum && (
                  <>
                    {' — '}
                    <span style={{ color: STATUS_COLORS.warning }}>
                      ниже минимума, к выплате <span className="tabular">{formatKop(line.baseKop)} ₽</span>
                    </span>
                  </>
                )}
              </span>
            )}
          </p>

        </div>

        </>
      )}

        {/*
          Премия — ОТДЕЛЬНЫМ полем, а не строчкой внутри блока ставки. Она к
          ставке не относится вовсе: начисляется фиксированной суммой и идёт
          сверх того, что дал расчёт, — сверх доли, если процент перерос
          гарантию, и сверх гарантии, если нет. В слабый месяц она и нужна.
        */}
        <div
          className="mt-2 flex items-center justify-between gap-3 rounded-lg border p-3"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
        >
          <span className="min-w-0">
            <span className="block text-xs text-ink2">Премия</span>
            <span className="text-[11px] text-ink2">за {monthLabel}</span>
          </span>
          <ValueField
            text={formatKop(bonusKop)}
            disabled={busy}
            onCommit={(raw) => onBonus(rublesToKop(raw))}
            suffix="₽"
          />
        </div>

      {/*
        АВАНС — ВИДИМЫМ полем, а не только внутри формы записи.

        Сначала он вводился лишь в форме «поставить сумму вручную», и найти
        его там, не зная о ней, было нельзя: пока аванс не записан, слова
        «аванс» в карточке не было вовсе. А выдают его каждый месяц —
        значит, ему место на виду, рядом с премией.

        Премия и аванс похожи полем, но противоположны по смыслу: премия
        добавляется к оплате, аванс из неё вычитается. Поэтому подпись
        называет не только срок, но и то, что он уменьшает выплату.
      */}
      <div
        className="mt-2 flex items-center justify-between gap-3 rounded-lg border p-3"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
      >
        <span className="min-w-0">
          <span className="block text-xs text-ink2">Аванс за {monthLabel}</span>
          <span className="text-[11px] text-ink2">выдан вперёд, вычитается из выплаты</span>
        </span>
        <ValueField
          text={formatKop(line.advanceKop)}
          onCommit={(raw) => onAdvance(rublesToKop(raw))}
          suffix="₽"
          disabled={busy}
        />
      </div>

      {/*
        ОТПУСКНЫЕ — деньги СВЕРХ заработанного.

        Доля считается от работ месяца, а в отпуске работ нет: покрывать
        отпуск долей не из чего. Поэтому отпускные прибавляются к итогу, а
        не вычитаются из него, как аванс, — и стоят рядом с ним, потому что
        оба привязаны к месяцу, а не к человеку вообще.

        Сумму за месяц, записанную вручную, из карточки убрали: её ставят
        редко и по особому случаю, а место она занимала постоянно. Она
        осталась в «Финансах», в разборе по сотрудникам.
      */}
      <div
        className="mt-2 flex items-center justify-between gap-3 rounded-lg border p-3"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
      >
        <span className="min-w-0">
          <span className="block text-xs text-ink2">Отпускные за {monthLabel}</span>
          <span className="text-[11px] text-ink2">сверх зарплаты за месяц</span>
        </span>
        <ValueField
          text={formatKop(line.vacationKop)}
          onCommit={(raw) => onVacation(rublesToKop(raw))}
          suffix="₽"
          disabled={busy}
        />
      </div>
    </div>
  );
}

/**
 * Разбор набранной ставки. `null` — «пока не число»: так выглядит пустое поле
 * и промежуточное «22,», и трогать сохранённое значение в этот момент нельзя.
 *
 * Дробные ставки допустимы: 22,5 % — обычное условие. В базе поле хранится
 * дробным, поэтому округлять до целого незачем; сотых хватает с запасом.
 */
function parsePercent(raw: string): number | null {
  const value = Number(raw.replace(',', '.').trim());
  if (raw.trim() === '' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, Math.round(value * 100) / 100));
}

